import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { announcementAPI, adminAPI } from '../lib/api';
import {
  LayoutDashboard,
  Search,
  Flame,
  Bell,
  Settings,
  CreditCard,
  Shield,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Zap,
  Sparkles,
  ShieldAlert,
  Crown,
  BookOpen,
  Info,
  Megaphone
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';

const navItems = [
  { path: '/dashboard', label: 'Tổng quan', icon: LayoutDashboard },
  { path: '/analyze', label: 'Check tương tác hot', icon: Search },
  { path: '/auto-search', label: 'Tự động tìm hot', icon: Zap },
  { path: '/ai-content', label: 'Content chuẩn SEO', icon: Sparkles },
  { path: '/bypass-content', label: 'Lách Content', icon: ShieldAlert },
  { path: '/hotface', label: 'Hotface List', icon: Flame },
  { path: '/notifications', label: 'Thông báo', icon: Bell },
  { path: '/tutorials', label: 'Hướng dẫn', icon: BookOpen },
  { path: '/billing', label: 'Gói VIP', icon: CreditCard },
  { path: '/about', label: 'Giới thiệu', icon: Info },
];


const adminItems = [
  { path: '/admin', label: 'Quản trị', icon: Shield },
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
    } catch { return []; }
  });
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [sessionDismissedPopups, setSessionDismissedPopups] = useState(() => {
    const saved = sessionStorage.getItem('session_dismissed_popups');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [annRes, settingsRes] = await Promise.all([
          announcementAPI.getActive(),
          adminAPI.getSiteSettings()
        ]);

        const activeAnnouncements = annRes.data || [];
        setAnnouncements(activeAnnouncements);
        setSiteSettings(settingsRes.data || {});

        // Show dialog if there are POPUP announcements and NOT dismissed in THIS session
        const visiblePopups = activeAnnouncements.filter(a => !sessionDismissedPopups.includes(a.id) && a.is_popup);
        if (visiblePopups.length > 0) {
          setAnnouncementDialogOpen(true);
        }

        // Set dynamic browser title
        if (settingsRes.data?.browser_title) {
          document.title = settingsRes.data.browser_title;
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissedAnnouncements.length]); // Re-check if length changes

  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));
  const unreadCount = visibleAnnouncements.length;
  // Popup visible in UI if not dismissed in session
  const visiblePopupAnnouncements = announcements.filter(a => a.is_popup && !sessionDismissedPopups.includes(a.id));

  useEffect(() => {
    if (location.pathname === '/notifications' && announcements.length > 0) {
      const allIds = announcements.map(a => a.id);
      if (allIds.some(id => !dismissedAnnouncements.includes(id))) {
        setDismissedAnnouncements(allIds);
        localStorage.setItem('dismissed_announcements', JSON.stringify(allIds));
      }
    }
  }, [location.pathname, announcements, dismissedAnnouncements]);

  const dismissAnnouncement = (id) => {
    if (!dismissedAnnouncements.includes(id)) {
      const newDismissed = [...dismissedAnnouncements, id];
      setDismissedAnnouncements(newDismissed);
      localStorage.setItem('dismissed_announcements', JSON.stringify(newDismissed));
    }

    // Also mark as dismissed for this session if it was a popup
    if (!sessionDismissedPopups.includes(id)) {
      const newSessionDismissed = [...sessionDismissedPopups, id];
      setSessionDismissedPopups(newSessionDismissed);
      sessionStorage.setItem('session_dismissed_popups', JSON.stringify(newSessionDismissed));
    }
  };

  const handleLogout = () => {
    logout();
    sessionStorage.clear();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    const item = [...navItems, ...adminItems].find(i => i.path === path);
    if (item) return item.label;
    if (path === '/settings') return 'Cài đặt';
    if (path === '/tutorials') return 'Hướng dẫn';
    return 'Dashboard';
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon overflow-hidden">
              {siteSettings.logo_url ? (
                <img
                  src={siteSettings.logo_url}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Flame size={24} />
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1C1E21]">{siteSettings.site_name?.split(' ')[0] || 'Hotface'}</h1>
              <p className="text-xs text-[#606770]">{siteSettings.site_subname || 'Analytics'}</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="px-3 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8C939D] px-3">
              Menu chính
            </span>
          </div>

          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <div className="relative flex items-center justify-center">
                <item.icon className="sidebar-item-icon" strokeWidth={2} />
                {item.path === '/notifications' && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 border-2 border-white"></span>
                )}
              </div>
              <span className="flex-1">{item.label}</span>
              {item.path === '/notifications' && unreadCount > 0 && (
                <Badge className="ml-auto bg-red-500 hover:bg-red-600 text-white border-0 text-[10px] px-1.5 h-5 rounded-full">
                  {unreadCount}
                </Badge>
              )}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="px-3 mb-2 mt-6">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8C939D] px-3">
                  Quản trị viên
                </span>
              </div>
              {adminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="sidebar-item-icon" strokeWidth={2} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="flex items-center gap-3 px-1.5">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#E7F3FF] text-[#1877F2] font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1C1E21] truncate">{user?.name}</p>
              <p className="text-xs text-[#606770] truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Header */}
        <header className="header" data-testid="header">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={20} />
            </Button>
            <h2 className="header-title">{getPageTitle()}</h2>
          </div>

          <div className="header-actions">
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border ${isAdmin || user?.subscription_type === 'monthly'
              ? "bg-amber-50 border-amber-100 text-amber-700"
              : "bg-slate-50 border-slate-100 text-slate-600"
              }`}>
              {isAdmin || user?.subscription_type === 'monthly' ? (
                <Crown size={16} className="text-amber-500" />
              ) : (
                <User size={16} className="text-slate-400" />
              )}
              <span className="text-sm font-semibold">
                {isAdmin ? "Admin Access" : (user?.subscription_type === 'monthly' ? "VIP Member" : "Standard Member")}
              </span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#1877F2] text-white text-sm font-semibold">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown size={16} className="text-[#606770]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings size={16} className="mr-2" />
                  Cài đặt
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="menu-billing">
                  <Crown size={16} className="mr-2 text-amber-500" />
                  Gói VIP
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" />
                  Đăng xuất
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <div className="page-content">
          {/* Announcement banners */}
          {visiblePopupAnnouncements.length > 0 && (
            <div className="space-y-2 mb-4" data-testid="announcements-banner">
              {visiblePopupAnnouncements.map((ann) => (
                <div
                  key={ann.id}
                  className="flex items-start gap-3 p-3 bg-[#E7F3FF] border border-[#1877F2]/20 rounded-xl animate-fadeIn"
                  data-testid={`announcement-${ann.id}`}
                >
                  <Megaphone size={18} className="text-[#1877F2] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1877F2]">{ann.title}</p>
                    <p className="text-sm text-[#606770] mt-0.5">{ann.content}</p>
                  </div>
                  <button
                    onClick={() => dismissAnnouncement(ann.id)}
                    className="text-[#8C939D] hover:text-[#1C1E21] flex-shrink-0"
                    data-testid={`dismiss-announcement-${ann.id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Announcement Popup Dialog */}
      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1877F2]">
              <Megaphone className="animate-bounce" />
              Thông báo từ hệ thống
            </DialogTitle>
            <DialogDescription>
              Vui lòng xem các cập nhật quan trọng bên dưới
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {visiblePopupAnnouncements.map((ann) => (
              <div key={ann.id} className="p-4 bg-blue-50 border border-blue-100 rounded-2xl space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-[#1877F2]">{ann.title}</h3>
                  <Badge className="bg-blue-100 text-blue-600 border-none text-[10px]">NEW</Badge>
                </div>
                <p className="text-sm text-[#606770] leading-relaxed whitespace-pre-wrap">{ann.content}</p>
                <div className="flex justify-end pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissAnnouncement(ann.id)}
                    className="text-xs text-[#8C939D] hover:text-[#1877F2] h-7"
                  >
                    Đã xem
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white font-bold"
              onClick={() => {
                // Dismiss all visible POPUP announcements when closing the global modal
                visiblePopupAnnouncements.forEach(ann => dismissAnnouncement(ann.id));
                setAnnouncementDialogOpen(false);
              }}
            >
              Tôi đã hiểu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
